import React, { useEffect, useMemo } from "react";

import {
  AlertCircle,
  Users,
  Building2,
  Calendar,
  Clock,
  TrendingUp,
  User,
  UserRound,
  Info,
  CheckCircle,
  ChevronRight,
  ArrowRight,
  Home,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import DailyPlaybookModal from "../../Components/GuidancePlaybooks/DailyPlaybookModal";
import TodaysPlaybookWidget from "../../Components/GuidancePlaybooks/TodaysPlaybookWidget";
import HomeVideosSection from "../../Components/HomeVideosSection";
import PageHeader from "../../Components/PageHeader";
import WelcomeBanner from "../../Components/WelcomeBanner";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchReviewAssessments,
  ReviewAssessment,
} from "../../Store/Reducers/AssessmentSlice";
import { fetchAssignedAccounts } from "../../Store/Reducers/CustomerWorkspaceSlice";
import { fetchDashboardCounts } from "../../Store/Reducers/MasterSlice";
import { fetchUserVideos } from "../../Store/Reducers/VideoSlice";
import {
  ROLE_DISPLAY_NAMES,
  ROLES,
  isRole,
  normalizeRoleName,
} from "../../Utils/Constants/RoleConstants";
import { isGuidanceHubActiveForUser } from "../../Utils/Guidance/guidanceHubSettings";
import {
  getTierBadgeClasses,
  useAccountTiers,
} from "../../Utils/Helper/accountTierHelpers";
import { formatDateTime } from "../../Utils/Helper/Helper";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import "./style.scss";

interface AssignedAccount {
  id: string;
  name: string;
  description: string;
  tier: string;
  type: "Primary" | "Secondary" | null;
}

interface PendingAssessment {
  id: string;
  title: string;
  dueDate: string;
  assignedBy: string;
  assignedDate: string;
  accountId: string;
  accountName: string;
  accountTier: string;
}

const ExecutiveSponsoreHome: React.FC = () => {
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const dispatch = useAppDispatch();
  const { formatTier } = useAccountTiers();

  const withOrg = (path: string) => {
    const p = path.startsWith("/") ? path : `/${path}`;
    return orgSlug ? `/${orgSlug}${p}` : p;
  };
  const { user } = useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const { reviewAssessments, reviewAssessmentsLoading, reviewAssessmentsMeta } =
    useAppSelector(state => state.assessment);
  const { dashboardCounts } = useAppSelector(state => state.master);
  const { assignedAccounts } = useAppSelector(state => state.customerWorkspace);
  const { userVideos, userVideosLoading } = useAppSelector(
    (state: any) => state.video
  );
  const { hubEnabled, hubEnabledRoles } = useAppSelector(
    state => state.guidance
  );

  const userName = loggedInUser?.fullName || user?.user?.full_name || "User";
  const organization = loggedInUser?.tenantName || user?.user?.tenant_domain;
  const userRole = user?.user?.role || loggedInUser?.role;
  const hubActive = isGuidanceHubActiveForUser(
    hubEnabled,
    hubEnabledRoles,
    userRole
  );
  const executiveOps = loggedInUser?.executiveOps ?? false;
  const executiveOpsApprove = loggedInUser?.executiveOpsApprove ?? false;
  const normalizedRole = normalizeRoleName(userRole);
  const isExecutiveUser = isRole(userRole, ROLES.EXECUTIVE);
  const baseRoleDisplayName =
    normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
      ? ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES]
      : "Executive Sponsore";

  const executiveId = loggedInUser?.userId ? String(loggedInUser.userId) : "";

  // Fetch review assessments and dashboard counts on mount
  useEffect(() => {
    if (executiveId) {
      dispatch(
        fetchReviewAssessments({
          executive_id: executiveId,
          tab: "assigned",
        })
      );
    }
    dispatch(fetchDashboardCounts());
    dispatch(fetchAssignedAccounts({}));

    // Fetch videos with role parameter
    const roleToFetch = normalizedRole || userRole || "executive_sponsor";
    dispatch(fetchUserVideos({ role: roleToFetch }) as any);
  }, [dispatch, executiveId, normalizedRole, userRole]);

  const videos = useMemo(
    () => (userVideos && userVideos.length > 0 ? userVideos : []),
    [userVideos]
  );
  const hasVideos = videos.length > 0;

  // Get pending assessments count from meta
  const pendingAssessmentsCount = useMemo(() => {
    return reviewAssessmentsMeta?.total_count || 0;
  }, [reviewAssessmentsMeta]);

  // Get assigned accounts count from API
  const assignedAccountsCount =
    assignedAccounts.total || dashboardCounts.data?.assigned_accounts || 0;

  // Include account count in role display
  const roleDisplayName = `${baseRoleDisplayName} • ${assignedAccountsCount} accounts assigned`;

  // Program Overview KPIs
  const programKPIs = [
    {
      title: "Executive Sponsors",
      value: dashboardCounts.data?.total_executive_sponsors ?? 0,
      icon: Users,
      colorClass: "text-blue-600",
      bgColorClass: "bg-blue-50",
    },
    {
      title: "Total Accounts",
      value: dashboardCounts.data?.total_accounts ?? 0,
      icon: Building2,
      colorClass: "text-blue-600",
      bgColorClass: "bg-blue-50",
    },
    {
      title: "Assigned Accounts",
      value: assignedAccountsCount,
      icon: Building2,
      colorClass: "text-purple-600",
      bgColorClass: "bg-purple-50",
    },
    {
      title: "Recent Assignments",
      value: dashboardCounts.data?.recent_assignments ?? 0,
      icon: Calendar,
      colorClass: "text-green-600",
      bgColorClass: "bg-green-50",
    },
    {
      title: "Completed Assessments",
      value: dashboardCounts.data?.completed_assessments ?? 0,
      icon: CheckCircle,
      colorClass: "text-orange-600",
      bgColorClass: "bg-orange-50",
    },
    {
      title: "Pending Assessments",
      value: pendingAssessmentsCount,
      icon: AlertCircle,
      colorClass: "text-red-600",
      bgColorClass: "bg-red-50",
    },
  ];

  const assignedAccountsList: AssignedAccount[] = useMemo(() => {
    if (!assignedAccounts.items || assignedAccounts.items.length === 0) {
      return [];
    }

    return assignedAccounts.items.map(account => ({
      id: account.customer_id,
      name: account.customer_name,
      description: account.industry || "Not specified",
      tier: account.tier || "N/A",
      type: (() => {
        const roleType = account.role_type?.toLowerCase();
        if (roleType === "primary") return "Primary";
        if (roleType === "secondary") return "Secondary";
        return null;
      })(),
    }));
  }, [assignedAccounts.items]);

  const assignedAccountsLoading = assignedAccounts.loading;

  // Map review assessments to pending assessments format, flattened by account
  const pendingAssessments: PendingAssessment[] = useMemo(() => {
    if (!reviewAssessments || reviewAssessments.length === 0) {
      return [];
    }

    const assessments: PendingAssessment[] = [];

    reviewAssessments.forEach((assessment: ReviewAssessment) => {
      // Format dates
      const dueDateFormatted = formatDateTime(
        assessment.due_date,
        "DMY_SHORT"
      ).date;
      const assignedDateFormatted = formatDateTime(
        assessment.created_at,
        "DMY_SHORT"
      ).date;

      // Flatten by accounts - create one entry per account
      if (assessment.accounts && assessment.accounts.length > 0) {
        assessment.accounts.forEach(account => {
          assessments.push({
            id: `${assessment.assessment_id}-${account.account_id}`,
            title: assessment.assessment_name,
            dueDate: dueDateFormatted,
            assignedBy: assessment.created_by,
            assignedDate: assignedDateFormatted,
            accountId: account.account_id,
            accountName: account.account_name,
            accountTier: account.account_tier || "N/A",
          });
        });
      } else {
        // If no accounts, still show the assessment
        assessments.push({
          id: assessment.assessment_id,
          title: assessment.assessment_name,
          dueDate: dueDateFormatted,
          assignedBy: assessment.created_by,
          assignedDate: assignedDateFormatted,
          accountId: "",
          accountName: "No Account",
          accountTier: "N/A",
        });
      }
    });

    return assessments;
  }, [reviewAssessments]);

  return (
    <div className="bg-white overflow-hidden mt-3">
      <PageHeader title="Home" icon={Home} />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4">
          <div className="executive-sponsore-home">
            <WelcomeBanner
              userName={userName}
              roleDisplayName={roleDisplayName}
              organization={organization}
              privileges={{
                executiveOps,
                executiveOpsApprove,
              }}
            />

            <DailyPlaybookModal userRole={userRole} />

            {/* Program Overview + Videos – side by side */}
            <div
              className={`grid grid-cols-1 ${
                hasVideos ? "lg:grid-cols-2" : "lg:grid-cols-1"
              } gap-6 mb-6`}
            >
              {/* Program Overview Section – left */}
              <div className="executive-sponsore-home__section bg-white rounded-lg p-6 border border-gray-200">
                <div className="mb-5">
                  <h2 className="text-xl font-semibold text-gray-900 mb-1 tracking-tight">
                    Program Overview
                  </h2>
                  <p className="text-sm text-gray-500">
                    Your sponsorship KPIs at a glance
                  </p>
                </div>
                <div
                  className={`grid ${hasVideos ? "grid-cols-2 gap-3 sm:gap-4" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4"}`}
                >
                  {programKPIs.map((kpi, index) => {
                    const IconComponent = kpi.icon;
                    return (
                      <div
                        key={index}
                        className={`${kpi.bgColorClass} rounded-xl p-4 flex items-center gap-3 min-h-[72px] sm:min-h-[80px] transition-shadow hover:shadow-sm`}
                      >
                        <div className={`${kpi.colorClass} shrink-0`}>
                          <IconComponent
                            className="w-5 h-5 sm:w-6 sm:h-6"
                            size={24}
                          />
                        </div>
                        <div className="min-w-0">
                          <div
                            className={`text-xs sm:text-sm font-medium ${kpi.colorClass}`}
                          >
                            {kpi.title}
                          </div>
                          <div
                            className={`text-xl sm:text-2xl font-semibold tabular-nums ${kpi.colorClass}`}
                          >
                            {kpi.value}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Videos Section – right */}
              {hasVideos && (
                <HomeVideosSection
                  videos={videos}
                  loading={userVideosLoading}
                  sectionClassName="executive-sponsore-home__section bg-white rounded-lg p-6 border border-gray-200 h-fit"
                  videoWrapClassName="executive-sponsore-home__video-wrap relative w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden bg-gray-100 mb-3"
                  placeholderClassName="executive-sponsore-home__video-placeholder w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-3 text-gray-500"
                  showDescription={false}
                />
              )}
            </div>

            {/* Your Accounts + Daily Playbook – side by side */}
            <div
              className={`grid grid-cols-1 ${
                hubActive ? "lg:grid-cols-2" : ""
              } gap-6 mb-6`}
            >
              <div className="executive-sponsore-home__section bg-white rounded-lg p-6 border border-gray-200 h-full">
                <div className="mb-5">
                  <div className="flex items-center justify-between gap-4">
                    <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
                      Your Accounts
                    </h2>
                    {isExecutiveUser && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            withOrg(
                              "/always-on-channel?accountTab=monitored&sidebarTab=others"
                            )
                          )
                        }
                        className="inline-flex items-center gap-1 text-sm font-semibold text-purple-600 hover:text-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 rounded shrink-0"
                      >
                        Go to Monitored Accounts
                        <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Accounts in your portfolio
                  </p>
                </div>
                {assignedAccountsLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <div className="animate-spin rounded-full h-9 w-9 border-2 border-gray-200 border-t-purple-600" />
                    <span className="mt-3 text-sm font-medium">
                      Loading assigned accounts…
                    </span>
                  </div>
                ) : assignedAccountsList.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Building2
                      className="w-12 h-12 text-gray-300 mb-3"
                      strokeWidth={1.5}
                    />
                    <span className="text-sm font-medium">
                      No assigned accounts
                    </span>
                    <span className="text-xs mt-1">
                      Accounts will appear here once assigned
                    </span>
                  </div>
                ) : (
                  <div
                    className={`executive-sponsore-home__accounts-grid thin-scrollbar-y grid ${
                      hubActive
                        ? "executive-sponsore-home__accounts-grid--compact grid-cols-2 xl:grid-cols-3"
                        : "executive-sponsore-home__accounts-grid--full grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
                    }`}
                  >
                    {assignedAccountsList.map(account => (
                      <div
                        key={account.id}
                        onClick={() =>
                          navigate(withOrg(`/account-synopsis/${account.id}`), {
                            state: {
                              from: withOrg("/home"),
                              fromLabel: "Home",
                            },
                          })
                        }
                        className={`executive-sponsore-home__card-clickable bg-white border border-gray-200 rounded-xl cursor-pointer ${
                          hubActive ? "p-3" : "p-4"
                        }`}
                      >
                        <div className="min-w-0">
                          <h3
                            className={`font-semibold text-gray-900 mb-1 truncate ${
                              hubActive ? "text-sm" : "text-base"
                            }`}
                          >
                            {account.name}
                          </h3>
                          <p
                            className={`text-gray-500 line-clamp-2 ${
                              hubActive ? "text-xs mb-2" : "text-sm"
                            }`}
                          >
                            {account.description}
                          </p>
                          <div
                            className={`flex flex-wrap items-center gap-1.5 ${
                              hubActive ? "" : "justify-end"
                            }`}
                          >
                            <span
                              className={`text-xs font-semibold rounded-md ${getTierBadgeClasses(account.tier)} ${
                                hubActive ? "px-2 py-0.5" : "px-2.5 py-1"
                              }`}
                            >
                              {formatTier(account.tier) ||
                                account.tier ||
                                "N/A"}
                            </span>
                            {account.type ? (
                              <span
                                className={`text-xs font-medium rounded-md ${
                                  account.type === "Primary"
                                    ? "bg-purple-100 text-purple-700"
                                    : "bg-gray-100 text-gray-600"
                                } ${hubActive ? "px-2 py-0.5" : "px-2.5 py-1"}`}
                              >
                                {account.type}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {hubActive && (
                <TodaysPlaybookWidget userRole={userRole} className="mb-0" />
              )}
            </div>

            {/* Quick Links Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div
                onClick={() => navigate(withOrg("/always-on-channel"))}
                className="executive-sponsore-home__card-clickable group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 rounded-lg p-3 group-hover:bg-purple-200/80 transition-colors">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      Always On Intelligence
                    </h3>
                    <p className="text-sm text-gray-500">
                      View real-time insights
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-purple-600 transition-colors" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/pending-assessments"))}
                className="executive-sponsore-home__card-clickable group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 rounded-lg p-3 group-hover:bg-green-200/80 transition-colors">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      My Assessments
                    </h3>
                    <p className="text-sm text-gray-500">
                      Complete pending assessments
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-green-600 transition-colors" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/goals-milestones"))}
                className="executive-sponsore-home__card-clickable group bg-white border border-gray-200 rounded-xl p-6 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 rounded- p-3 group-hover:bg-blue-200/80 transition-colors">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      My Progress
                    </h3>
                    <p className="text-sm text-gray-500">
                      Track your milestones
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-blue-600 transition-colors" />
                </div>
              </div>
            </div>

            {/* Pending Assessments Section */}
            <div className="executive-sponsore-home__section bg-white rounded-lg p-6 border border-gray-200">
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
                    Pending Assessments
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Assessments awaiting your review
                  </p>
                </div>
                {pendingAssessments.length > 0 && (
                  <button
                    type="button"
                    onClick={() => navigate(withOrg("/pending-assessments"))}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-purple-600 hover:text-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 rounded"
                  >
                    See all
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
              {reviewAssessmentsLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <div className="animate-spin rounded-full h-9 w-9 border-2 border-gray-200 border-t-purple-600" />
                  <span className="mt-3 text-sm font-medium">
                    Loading assessments…
                  </span>
                </div>
              ) : pendingAssessments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <CheckCircle
                    className="w-12 h-12 text-gray-300 mb-3"
                    strokeWidth={1.5}
                  />
                  <span className="text-sm font-medium">
                    No pending assessments
                  </span>
                  <span className="text-xs mt-1">You’re all caught up</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {pendingAssessments.slice(0, 4).map(assessment => (
                    <div
                      key={assessment.id}
                      onClick={() => navigate(withOrg("/pending-assessments"))}
                      className="executive-sponsore-home__card-clickable bg-white border border-gray-200 rounded-lg p-4 cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="bg-purple-100 rounded-lg p-2 shrink-0">
                            <Info className="w-4 h-4 text-purple-600" />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 line-clamp-2">
                            {assessment.title}
                          </span>
                        </div>
                        <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full shrink-0">
                          Pending
                        </span>
                      </div>

                      {/* Account Name Section */}
                      <div className="mb-3 flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <span className="text-sm font-medium text-blue-600 truncate">
                          {assessment.accountName}
                        </span>
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded shrink-0 ${getTierBadgeClasses(assessment.accountTier)}`}
                        >
                          {formatTier(assessment.accountTier) ||
                            assessment.accountTier}
                        </span>
                      </div>

                      <div className="space-y-2 text-xs text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span>
                            <span className="font-medium text-gray-600">
                              Due:
                            </span>{" "}
                            {assessment.dueDate}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <UserRound className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span>
                            <span className="font-medium text-gray-600">
                              By:
                            </span>{" "}
                            {assessment.assignedBy}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <span>
                            <span className="font-medium text-gray-600">
                              Assigned:
                            </span>{" "}
                            {assessment.assignedDate}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSponsoreHome;
